import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/empty/EmptyState";
import { Button } from "../components/ui/button";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { canExportConsents } from "../lib/crm-permissions";
import { getConsentSourceLabel, getConsentTypeLabel } from "../lib/labels";
import { toast } from "sonner";

const PAGE_SIZE = 20;

function getStatusLabel(status) {
  if (status === "granted") return "Erlaubt";
  if (status === "revoked") return "Widerrufen";
  return "Nicht erteilt";
}

function formatDateTime(value) {
  if (!value) return "Noch keine Änderung";
  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function mergeItems(existing, incoming) {
  const byId = new Map();
  for (const item of [...existing, ...incoming]) {
    byId.set(item.contact_id, item);
  }
  return Array.from(byId.values());
}

export default function ConsentsPage() {
  const { role, isSuperAdmin, tenant } = useAuth();
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    consentType: "any",
    status: "any",
    source: "",
  });
  const [selectedContactId, setSelectedContactId] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailEvents, setDetailEvents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const exportAllowed = canExportConsents({ role, isSuperAdmin });
  const missingTenantContext = Boolean(isSuperAdmin && !tenant?.id);
  const hasActiveFilters = Boolean(
    filters.search || filters.source || filters.consentType !== "any" || filters.status !== "any"
  );

  const buildParams = useCallback((cursor = null) => {
    const params = { limit: PAGE_SIZE };
    if (filters.search) params.search = filters.search;
    if (filters.consentType !== "any") params.consent_type = filters.consentType;
    if (filters.status !== "any") params.status = filters.status;
    if (filters.source) params.source = filters.source;
    if (cursor?.last_changed_at && cursor?.contact_id) {
      params.before_last_changed_at = cursor.last_changed_at;
      params.before_contact_id = cursor.contact_id;
    }
    return params;
  }, [filters]);

  const loadPage = useCallback(async (cursor = null, append = false) => {
    if (append) setLoadingMore(true);
    else {
      setLoadState("loading");
      setError("");
    }
    try {
      const response = await api.get("/api/consents/page", { params: buildParams(cursor) });
      const page = response.data || { items: [], next_cursor: null, has_more: false };
      setItems((prev) => (append ? mergeItems(prev, page.items || []) : (page.items || [])));
      setNextCursor(page.next_cursor || null);
      setHasMore(Boolean(page.has_more));
      if (!append) setLoadState("ready");
    } catch (err) {
      const message = err?.response?.data?.detail
        || extractApiError(err, "Einwilligungen konnten nicht geladen werden.")
        || "Einwilligungen konnten nicht geladen werden.";
      setError(message);
      if (!append) {
        setItems([]);
        setNextCursor(null);
        setHasMore(false);
        setLoadState("error");
      }
    } finally {
      if (append) setLoadingMore(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (missingTenantContext) {
      setItems([]);
      setNextCursor(null);
      setHasMore(false);
      setLoadState("ready");
      setSelectedContactId("");
      setDetail(null);
      setDetailEvents([]);
      setError("");
      return;
    }
    setSelectedContactId("");
    setDetail(null);
    setDetailEvents([]);
    loadPage();
  }, [loadPage, missingTenantContext]);

  const handleResetFilters = () => {
    setFilters({
      search: "",
      consentType: "any",
      status: "any",
      source: "",
    });
  };

  const handleToggleDetails = async (contactId) => {
    if (selectedContactId === contactId) {
      setSelectedContactId("");
      setDetail(null);
      setDetailEvents([]);
      setDetailError("");
      return;
    }
    setSelectedContactId(contactId);
    setDetailLoading(true);
    setDetailError("");
    try {
      const [detailResponse, eventsResponse] = await Promise.all([
        api.get(`/api/consents/${contactId}`),
        api.get(`/api/consents/${contactId}/events`, { params: { limit: PAGE_SIZE } }),
      ]);
      setDetail(detailResponse.data);
      setDetailEvents(eventsResponse.data?.items || []);
    } catch (err) {
      setDetail(null);
      setDetailEvents([]);
      setDetailError(extractApiError(err, "Historie konnte nicht geladen werden."));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = useCallback(async () => {
    try {
      const response = await api.get("/api/consents/export.csv", {
        params: buildParams(),
        responseType: "blob",
      });
      const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data || ""]);
      const url = window.URL?.createObjectURL ? window.URL.createObjectURL(blob) : "";
      const link = document.createElement("a");
      link.href = url;
      link.download = "consents.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (url && window.URL?.revokeObjectURL) {
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error(extractApiError(err, "CSV-Export fehlgeschlagen."));
    }
  }, [buildParams]);

  const actions = useMemo(() => (
    exportAllowed ? (
      <Button
        type="button"
        onClick={handleExport}
        data-testid="consents-export-button"
      >
        CSV-Export
      </Button>
    ) : null
  ), [exportAllowed, handleExport]);

  const contextHint = useMemo(() => {
    if (missingTenantContext) {
      return "Systemansicht";
    }
    if (isSuperAdmin && tenant?.name) {
      return `Aktiver Mandant: ${tenant.name}`;
    }
    return "";
  }, [isSuperAdmin, missingTenantContext, tenant]);

  return (
    <div>
      <PageHeader
        title="Einwilligungen"
        description="Hier lassen sich Marketing-, Tracking- und Datenschutz-Einwilligungen tenantbezogen nachvollziehen."
        actions={actions}
      />

      {contextHint ? (
        <div className="mb-4 rounded-xl border border-border bg-card/60 px-4 py-3 text-sm">
          {contextHint}
        </div>
      ) : null}

      <div className="surface rounded-xl p-4 mb-4 space-y-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <input
            type="text"
            value={filters.search}
            placeholder="Suche nach Kontakt, Firma oder E-Mail"
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            data-testid="consents-search-input"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <select
            value={filters.consentType}
            onChange={(event) => setFilters((prev) => ({ ...prev, consentType: event.target.value }))}
            data-testid="consents-type-filter"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="any">Alle</option>
            <option value="marketing">Marketing-Kommunikation</option>
            <option value="tracking">E-Mail-Tracking</option>
          </select>
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            data-testid="consents-status-filter"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="any">Alle</option>
            <option value="granted">Erlaubt</option>
            <option value="revoked">Widerrufen</option>
            <option value="not_granted">Nicht erteilt</option>
          </select>
          <input
            type="text"
            value={filters.source}
            placeholder="Quelle"
            onChange={(event) => setFilters((prev) => ({ ...prev, source: event.target.value }))}
            data-testid="consents-source-filter"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        {hasActiveFilters ? (
          <div>
            <Button type="button" variant="outline" onClick={handleResetFilters} data-testid="consents-reset-button">
              Filter zurücksetzen
            </Button>
          </div>
        ) : null}
      </div>

      <div className="surface rounded-xl p-4">
        {missingTenantContext ? (
          <EmptyState
            testId="consents-no-tenant-state"
            title="Bitte wähle zuerst einen Mandanten aus, um Einwilligungen anzuzeigen."
            description="Ohne aktiven Mandanten zeigt das Consent Center keine tenantbezogenen CRM-Daten an."
            actionLabel="Zur Mandantenverwaltung"
            onAction={() => {
              window.location.href = "/admin/tenants";
            }}
          />
        ) : loadState === "loading" ? (
          <div className="py-8 text-sm text-muted-foreground text-center">Wird geladen ...</div>
        ) : loadState === "error" ? (
          <div className="py-8 text-sm text-destructive text-center">{error}</div>
        ) : items.length === 0 ? (
          isSuperAdmin ? (
            <EmptyState
              testId="consents-empty-state"
              title="Keine Einwilligungen gefunden"
              description="Für den aktuell ausgewählten Mandanten wurden keine Einwilligungen gefunden."
            />
          ) : (
            <EmptyState
              testId="consents-empty-state"
              title="Keine Einwilligungen gefunden"
              description="Passen Sie die Filter an oder prüfen Sie spätere Änderungen erneut."
            />
          )
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto pb-1">
              <table className="w-full min-w-[1120px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Kontext</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Kontakt</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Firma</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">E-Mail</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Marketing</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Tracking</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Datenschutz / Anfrage</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Letzte Änderung</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Letzte Quelle</th>
                    <th className="px-3 py-3 font-medium whitespace-nowrap">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <React.Fragment key={item.contact_id}>
                      <tr className="border-b border-border/60 align-top">
                        <td className="px-3 py-3 align-top text-sm text-foreground/90 whitespace-nowrap">
                          {item.subject_label || "Kontakt"}
                        </td>
                        <td className="px-3 py-3 align-top text-sm [overflow-wrap:anywhere]">
                          {item.contact_name}
                        </td>
                        <td className="px-3 py-3 align-top text-sm [overflow-wrap:anywhere]">
                          {item.company_name || "—"}
                        </td>
                        <td
                          className="px-3 py-3 align-top text-sm break-words [overflow-wrap:anywhere]"
                          data-testid={`consents-email-cell-${item.contact_id}`}
                        >
                          {item.email}
                        </td>
                        <td className="px-3 py-3 align-top text-sm whitespace-nowrap">
                          {getStatusLabel(item.marketing_status)}
                        </td>
                        <td className="px-3 py-3 align-top text-sm whitespace-nowrap">
                          {getStatusLabel(item.tracking_status)}
                        </td>
                        <td className="px-3 py-3 align-top text-sm whitespace-nowrap">
                          {getStatusLabel(item.privacy_status)}
                        </td>
                        <td className="px-3 py-3 align-top text-sm whitespace-nowrap text-muted-foreground">
                          {formatDateTime(item.last_changed_at)}
                        </td>
                        <td className="px-3 py-3 align-top text-sm [overflow-wrap:anywhere]">
                          {getConsentSourceLabel(item.last_source) || "—"}
                        </td>
                        <td className="px-3 py-3 align-top whitespace-nowrap">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleDetails(item.contact_id)}
                            data-testid={`consents-detail-button-${item.contact_id}`}
                          >
                            {selectedContactId === item.contact_id ? "Schließen" : "Details"}
                          </Button>
                        </td>
                      </tr>
                      {selectedContactId === item.contact_id ? (
                        <tr className="border-b border-border/60">
                          <td colSpan={10} className="px-3 py-4 bg-muted/10">
                            {detailLoading ? (
                              <div className="text-sm text-muted-foreground">Historie wird geladen ...</div>
                            ) : detailError ? (
                              <div className="text-sm text-destructive">{detailError}</div>
                            ) : detail ? (
                              <div
                                className="rounded-xl border border-border bg-muted/20 p-4 md:p-5 space-y-4 min-w-0 max-w-full"
                                data-testid={`consents-detail-panel-${item.contact_id}`}
                              >
                                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 min-w-0">
                                  <div className="min-w-0 space-y-1">
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                      Kontext
                                    </div>
                                    <div className="text-sm font-medium [overflow-wrap:anywhere]">
                                      {detail.subject_label || item.subject_label || "Kontakt"}
                                    </div>
                                  </div>
                                  <div className="min-w-0 space-y-1">
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                      Marketing
                                    </div>
                                    <div className="text-sm font-medium">{getStatusLabel(detail.marketing_status)}</div>
                                  </div>
                                  <div className="min-w-0 space-y-1">
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                      Tracking
                                    </div>
                                    <div className="text-sm font-medium">{getStatusLabel(detail.tracking_status)}</div>
                                  </div>
                                  <div className="min-w-0 space-y-1">
                                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                      Datenschutz / Anfrage
                                    </div>
                                    <div className="text-sm font-medium">{getStatusLabel(detail.privacy_status)}</div>
                                  </div>
                                </div>
                                <div className="min-w-0">
                                  <div className="text-sm font-medium mb-3">Historie</div>
                                  {detailEvents.length === 0 ? (
                                    <div className="text-sm text-muted-foreground">Keine Historie vorhanden.</div>
                                  ) : (
                                    <div className="space-y-3">
                                      {detailEvents.map((event, index) => (
                                        <div
                                          key={`${event.occurred_at}-${index}`}
                                          className="rounded-lg border border-border bg-background px-4 py-3 min-w-0 max-w-full [overflow-wrap:anywhere]"
                                          data-testid={`consents-history-card-${index}`}
                                        >
                                          <div className="font-medium [overflow-wrap:anywhere]">
                                            {getConsentTypeLabel(event.consent_type)} · {getStatusLabel(event.status)}
                                          </div>
                                          <div className="mt-1 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                                            {getConsentSourceLabel(event.source)} · {formatDateTime(event.occurred_at)}
                                          </div>
                                          {event.changed_by_user_name ? (
                                            <div className="mt-2 text-sm [overflow-wrap:anywhere]">
                                              {event.changed_by_user_name}
                                            </div>
                                          ) : null}
                                          {event.consent_text_snapshot ? (
                                            <div className="mt-2 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                                              {event.consent_text_snapshot}
                                            </div>
                                          ) : null}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-center">
              {hasMore ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => loadPage(nextCursor, true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Weitere Einwilligungen werden geladen ..." : "Mehr laden"}
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
